/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Person, PersonCardInteraction, ViewType } from "@microsoft/mgt-react";
import IntelLogo from "../logo.svg";
import { useBoolean } from "@fluentui/react-hooks";
import {
  Icon,
  Separator,
  Panel,
  PanelType,
  TooltipHost,
  TooltipDelay,
  DirectionalHint,
} from "@fluentui/react";
import { useState } from "react";
import { activityLog } from "../objects/activityLog";
import { calculateTimePassed } from "../utils";
import { apiScopes, m365AppsBaseLinks } from "../authConfig";
import Swal from "sweetalert2";
import { renderToStaticMarkup } from "react-dom/server";
import { Bounce, toast } from "react-toastify";
import { useMsal } from "@azure/msal-react";
import { addUserFeedBackEntry } from "../services/resource-manager";
import StatusCode from "status-code-enum";
import { NavLink } from "react-router-dom";

/**
 * Navbar component, has navigations links and implements a persona component to display current user data
 */
const MainNavbar = (props: { userName: string; userActivity: Array<activityLog> }) => {
  const { instance, accounts } = useMsal();
  const accessTokenRequest = {
    scopes: apiScopes.resourceManagementScopes,
    account: accounts[0],
  };
  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] = useBoolean(false);
  const [notificationsPending, setNotificationsPending] = useState<boolean>(true);

  async function openFeedbackForm() {
    let numericValue: number = 0;
    let textValue: string = "";
    let htmlDialogContent = (
      <div>
        <p>How would you rate your experience?</p>
        <div
          className="rate swal2-input"
          style={{
            width: "100%",
            margin: "0",
            fontSize: "xx-large",
            borderWidth: "0",
            boxShadow: "none",
          }}
        >
          <input type="radio" id="star5" name="userFeedbackScore" value="5" />
          <label
            htmlFor="star5"
            style={{ width: "20%" }}
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Amazing"
          ></label>
          <input type="radio" id="star4" name="userFeedbackScore" value="4" />
          <label
            htmlFor="star4"
            style={{ width: "20%" }}
            data-toggle="tooltip"
            data-placement="top"
            title="Very Good"
          ></label>
          <input type="radio" id="star3" name="userFeedbackScore" value="3" />
          <label
            htmlFor="star3"
            style={{ width: "20%" }}
            data-toggle="tooltip"
            data-placement="top"
            title="Good"
          ></label>
          <input type="radio" id="star2" name="userFeedbackScore" value="2" />
          <label
            htmlFor="star2"
            style={{ width: "20%" }}
            data-toggle="tooltip"
            data-placement="top"
            title="Average"
          ></label>
          <input type="radio" id="star1" name="userFeedbackScore" value="1" checked />
          <label
            htmlFor="star1"
            style={{ width: "20%" }}
            data-toggle="tooltip"
            data-placement="top"
            title="Needs Work"
          ></label>
        </div>
        <br></br>
        <p>Do you have any additional comment??</p>
        <textarea
          id="txtUserFeedbackComments"
          style={{ width: "100%", margin: "0" }}
          className="swal2-textarea"
          rows={5}
          placeholder="Enter it here"
          maxLength={500}
        ></textarea>
      </div>
    );
    Swal.fire({
      title: "We value your feedback",
      html: renderToStaticMarkup(htmlDialogContent),
      showCancelButton: true,
      confirmButtonText: "Submit",
      preConfirm: () => {
        const selectedRadio = document.querySelector(
          'input[name="userFeedbackScore"]:checked'
        ) as HTMLInputElement;
        const textareaElement = document.getElementById(
          "txtUserFeedbackComments"
        ) as HTMLTextAreaElement;

        if (selectedRadio) {
          numericValue = Number(selectedRadio.value);
        }
        if (textareaElement) {
          textValue = textareaElement.value;
        }
        if (numericValue !== 0 || textValue !== "") {
          instance
            .acquireTokenSilent(accessTokenRequest)
            .then(async (accessTokenResponse) => {
              let operationResult = await addUserFeedBackEntry(
                accessTokenResponse.accessToken,
                numericValue,
                textValue
              );
              if (operationResult !== StatusCode.SuccessOK) {
                console.error("Error while registering user feedback");
              }
            })
            .catch((error) => {
              console.error(error);
            });
        }
        toast("⭐ Thank your for your valuable feedback", {
          position: "top-right",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      },
    });
  }

  function getResourceActivityLink(activityEntry: activityLog, resourceConnected: boolean): string {
    if (resourceConnected) {
      return `/resources?isconnected=y&resourceid=${activityEntry.impactedResourceID}`;
    } else {
      if (activityEntry.siteID === "") {
        return activityEntry.impactedResourceID;
      } else {
        return `/resources?isconnected=n&resourceid=${activityEntry.siteID}`;
      }
    }
  }

  function parseActivityList(): Array<any> {
    let tempArray: Array<any> = [];
    let tempArrayCount: number = 1;
    props.userActivity.forEach((activityEntry) => {
      let activityIconTemp: string = "Message";
      let activityDescriptionTemp = "";
      let timeStampTemp = calculateTimePassed(activityEntry.activityDateTime);
      let activityDetail: JSX.Element[] = [];

      if (
        activityEntry.actionTaken === "AddOwnerM365" ||
        activityEntry.actionTaken === "AddOwnerSPO"
      ) {
        activityIconTemp = "AddFriend";
        activityDescriptionTemp = "Owner or admin added";
        activityDetail = [
          <span key={1}>
            A new owner or site collection admin was added to{" "}
            <a
              href={getResourceActivityLink(
                activityEntry,
                activityEntry.actionTaken === "AddOwnerM365"
              )}
            >
              one of your resources
            </a>
          </span>,
        ];
      } else if (
        activityEntry.actionTaken === "RemoveOwnerM365" ||
        activityEntry.actionTaken === "RemoveOwnerSPO"
      ) {
        activityIconTemp = "UserRemove";
        activityDescriptionTemp = "Owner or Admin removed";
        activityDetail = [
          <span key={1}>
            A new owner or site collection admin was removed from{" "}
            <a
              href={getResourceActivityLink(
                activityEntry,
                activityEntry.actionTaken === "RemoveOwnerM365"
              )}
            >
              one of your resources
            </a>
          </span>,
        ];
      } else if (
        activityEntry.actionTaken === "LeaseExtendM365" ||
        activityEntry.actionTaken === "LeaseExtendSPO"
      ) {
        activityIconTemp = "Clock";
        activityDescriptionTemp = "Lease extended";
        activityDetail = [
          <span key={1}>
            6 months of additional resource lease were added to{" "}
            <a
              href={getResourceActivityLink(
                activityEntry,
                activityEntry.actionTaken === "LeaseExtendM365"
              )}
            >
              one of your resources
            </a>
          </span>,
        ];
      } else if (
        activityEntry.actionTaken === "UpdateSensitivityLabelM365" ||
        activityEntry.actionTaken === "UpdateSensitivityLabelSPO"
      ) {
        activityIconTemp = "Label";
        activityDescriptionTemp = "Label changed";
        activityDetail = [
          <span key={1}>
            The sensitivity label changed from {activityEntry.oldValue} to {activityEntry.newValue}{" "}
            for{" "}
            <a
              href={getResourceActivityLink(
                activityEntry,
                activityEntry.actionTaken === "UpdateSensitivityLabelM365"
              )}
            >
              one of your resources
            </a>
          </span>,
        ];
      } else if (
        activityEntry.actionTaken === "DeleteM365Group" ||
        activityEntry.actionTaken === "DeleteSPOSite"
      ) {
        activityIconTemp = "Delete";
        activityDescriptionTemp = "Workspace deleted";
        activityDetail = [
          <span key={1}>
            You deleted a{" "}
            {activityEntry.actionTaken === "DeleteM365Group"
              ? "connected resource (Yammer Community, Team or SharePoint site)"
              : "SharePoint site"}
            : {activityEntry.oldValue?.replace(`${m365AppsBaseLinks.sharepoint}/sites/`, "")}
          </span>,
        ];
      } else if (activityEntry.actionTaken === "UpdateWorkspaceDepartment") {
        activityIconTemp = "EditNote";
        activityDescriptionTemp = "Department updated";
        activityDetail = [
          <span key={1}>
            The department for one of your workspaces was updated from{" "}
            <strong>{activityEntry.oldValue}</strong> to{" "}
            <strong>{activityEntry.newValue}</strong>
          </span>,
        ];
      } else if (activityEntry.actionTaken === "ArchiveWorkspace") {
        activityIconTemp = "Archive";
        activityDescriptionTemp = "Workspace archived";
        activityDetail = [
          <span key={1}>
            A workspace was archived:{" "}
            <strong>
              {activityEntry.impactedResourceID?.replace(
                `${m365AppsBaseLinks.sharepoint}/sites/`,
                ""
              ) ?? activityEntry.impactedResourceID}
            </strong>
          </span>,
        ];
      } else {
        // Fallback for any unrecognized action types
        activityIconTemp = "Info";
        activityDescriptionTemp = activityEntry.actionTaken ?? "Activity recorded";
        activityDetail = [
          <span key={1}>
            An action (<strong>{activityEntry.actionTaken}</strong>) was performed on a resource.
            {activityEntry.oldValue && activityEntry.newValue && (
              <>
                {" "}
                Changed from <strong>{activityEntry.oldValue}</strong> to{" "}
                <strong>{activityEntry.newValue}</strong>.
              </>
            )}
          </span>,
        ];
      }

      tempArray.push({
        key: tempArrayCount,
        activityDescription: activityDescriptionTemp,
        activityIcon: <Icon iconName={activityIconTemp} />,
        comments: activityDetail,
        timeStamp: timeStampTemp,
      });

      tempArrayCount++;
    });
    return tempArray;
  }

  function toggleActivityPanel(): void {
    openPanel();
    setNotificationsPending(false);
  }

  let activityLogList = parseActivityList();

  return (
    <header>
      <Panel
        headerText="Recent Activity"
        type={PanelType.smallFixedFar}
        isOpen={isOpen}
        onDismiss={dismissPanel}
        onOpen={() => {
          const button = document.getElementById("btnActivityPanelToggle");
          if (button instanceof HTMLElement) {
            button.blur();
          }
        }}
        closeButtonAriaLabel="Close"
      >
        <Separator />

        {activityLogList.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              marginTop: "60px",
              color: "#605e5c",
              gap: "12px",
            }}
          >
            <Icon iconName="Ringer" style={{ fontSize: "48px", color: "#c8c6c4" }} />
            <span style={{ fontSize: "16px", fontWeight: 600 }}>No recent activity</span>
            <span style={{ fontSize: "13px", textAlign: "center", color: "#a19f9d" }}>
              Actions you take on your workspaces will appear here.
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {activityLogList.map(
              (item: {
                key: string | number;
                activityDescription: string;
                activityIcon: JSX.Element;
                comments: JSX.Element[];
                timeStamp: string;
              }) => (
                <div
                  key={item.key}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    padding: "12px 8px",
                    borderRadius: "6px",
                    borderBottom: "1px solid #edebe9",
                    gap: "12px",
                    transition: "background 0.15s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f2f1")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  {/* Icon bubble */}
                  <div
                    style={{
                      flexShrink: 0,
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: "#e1dfdd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#323130",
                      fontSize: "16px",
                    }}
                  >
                    {item.activityIcon}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "4px",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "#323130",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {item.activityDescription}
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          color: "#a19f9d",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {item.timeStamp}
                      </span>
                    </div>
                    <div style={{ fontSize: "12px", color: "#605e5c", lineHeight: "1.4" }}>
                      {item.comments}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </Panel>

      <nav className="navbar navbar-expand-xl navbar-dark bg-primary">
        <div className="container-fluid">
          <style></style>
          <a className="navbar-brand" href="https://m365management.intel.com/">
            <img
              className="d-inline-block align-text-top me-2"
              height="23"
              src={IntelLogo}
              alt="Intel"
            />
            Workspace Management Tool <span style={{ fontSize: "10px" }}>1.3.1</span>
          </a>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarCollapse"
            aria-controls="navbarCollapse"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarCollapse">
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <TooltipHost
                  content="Go to your workspaces home page"
                  delay={TooltipDelay.zero}
                  directionalHint={DirectionalHint.bottomCenter}
                >
                  <NavLink to="/" className="nav-link text-nowrap d-flex align-items-center">
                    <Icon iconName="Home" style={{ fontSize: "16px", marginRight: "8px" }} />
                    My Workspaces
                  </NavLink>
                </TooltipHost>
              </li>

              <li className="nav-item">
                <TooltipHost
                  content="View your archived workspaces"
                  delay={TooltipDelay.zero}
                  directionalHint={DirectionalHint.bottomCenter}
                >
                  <NavLink
                    to="/archived"
                    className="nav-link text-nowrap d-flex align-items-center"
                  >
                    <Icon iconName="Archive" style={{ fontSize: "16px", marginRight: "8px" }} />
                    Archived
                  </NavLink>
                </TooltipHost>
              </li>

              <li className="nav-item">
                <TooltipHost
                  content="Request a new workspace"
                  delay={TooltipDelay.zero}
                  directionalHint={DirectionalHint.bottomCenter}
                >
                  <a
                    className="nav-link text-nowrap d-flex align-items-center"
                    href="https://apps.powerapps.com/play/e/c57452c8-1d59-4cc7-bb06-dd1bd68f06bf/a/44b067d3-ed7a-4b63-8933-c48670ed9a14?tenantId=46c98d88-e344-4ed4-8496-4ed7712e255d"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon iconName="Add" style={{ fontSize: "16px", marginRight: "8px" }} />
                    New Workspace
                  </a>
                </TooltipHost>
              </li>

              <li className="nav-item">
                <TooltipHost
                  content="Visit the Workspace Management Tool help page"
                  delay={TooltipDelay.zero}
                  directionalHint={DirectionalHint.bottomCenter}
                >
                  <a
                    className="nav-link text-nowrap d-flex align-items-center"
                    href="https://intel.sharepoint.com/sites/O365Help/SitePages/Workspace-Management-Tool.aspx"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon iconName="Help" style={{ fontSize: "16px", marginRight: "8px" }} />
                    Help
                  </a>
                </TooltipHost>
              </li>

              <li className="nav-item">
                <TooltipHost
                  content="Share your feedback about the Workspace Management Tool"
                  delay={TooltipDelay.zero}
                  directionalHint={DirectionalHint.bottomCenter}
                >
                  <a
                    id="btnOpenFeedbackForm"
                    className="nav-link text-nowrap d-flex align-items-center"
                    href="javascript:void(0)"
                    onClick={async () => {
                      openFeedbackForm();
                    }}
                    rel="noreferrer"
                  >
                    <Icon iconName="Feedback" style={{ fontSize: "16px", marginRight: "8px" }} />
                    Feedback
                  </a>
                </TooltipHost>
              </li>
            </ul>

            <ul className="navbar-nav justify-content-end" style={{ marginRight: "5px" }}>
              <TooltipHost
                content="View your recent workspace activity and notifications"
                delay={TooltipDelay.zero}
                directionalHint={DirectionalHint.bottomCenter}
              >
                <button
                  type="button"
                  className="btn nav-link position-relative shadow-none d-flex align-items-center"
                  style={{
                    textAlign: "left",
                    minWidth: "30px",
                  }}
                  onClick={toggleActivityPanel}
                  id="btnActivityPanelToggle"
                >
                  <Icon
                    iconName="Ringer"
                    style={{
                      fontSize: "16px",
                      marginRight: "8px",
                      flexShrink: 0,
                    }}
                  />
                  <span className="d-none d-lg-inline-block d-md-inline-block d-sm-inline-block d-xs-inline-block d-xl-none">
                    Notifications
                  </span>
                  <div
                    id="navbarPillContainer"
                    style={{ display: notificationsPending ? "inline-block" : "none" }}
                  >
                    <span
                      className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-secondary"
                      style={{ fontSize: "xx-small", marginTop: "10px" }}
                    >
                      !
                    </span>
                  </div>
                </button>
              </TooltipHost>
            </ul>

            <ul className="navbar-nav justify-content-end">
              <li className="nav-item">
                <TooltipHost
                  content={`Signed in as ${props.userName}`}
                  delay={TooltipDelay.zero}
                  directionalHint={DirectionalHint.bottomCenter}
                >
                  <div className="nav-item-padding-persona">
                    <table>
                      <tbody>
                        <tr>
                          <td>
                            <Person
                              personQuery="me"
                              className="person-element"
                              id="avatarElement"
                              showPresence
                              view={ViewType.image}
                              personCardInteraction={PersonCardInteraction.hover}
                            ></Person>
                          </td>
                          <td>
                            <label style={{ color: "white", marginLeft: "7px" }}>
                              {" "}
                              {props.userName}
                            </label>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </TooltipHost>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default MainNavbar;